import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Rocket, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

/**
 * AppSumo license redemption — landed after OAuth callback redirects here with ?session=
 */
export default function AuthAppSumo({ setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('session') || '').trim();
  }, [location.search]);

  const [mode, setMode] = useState('register');
  const [loading, setLoading] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionError, setSessionError] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });

  useEffect(() => {
    if (!sessionId) {
      setSessionError('Missing redemption session. Activate your license again from AppSumo.');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/appsumo/session/${encodeURIComponent(sessionId)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (!cancelled) setSessionError(data.detail || 'Redemption session invalid or expired');
          return;
        }
        if (!cancelled) {
          setSessionInfo(data);
          if (data.already_linked) setMode('login');
        }
      } catch (e) {
        if (!cancelled) setSessionError('Could not load redemption session');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const planLabel = (sessionInfo?.plan || 'starter');
  const planTitle = planLabel === 'growth' ? 'Growth' : planLabel === 'basic' ? 'Basic' : 'Starter';

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId) return;
    if (mode === 'register' && !acceptTerms) {
      toast.error('Accept the Terms of Service to continue');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/appsumo/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session: sessionId,
          email: formData.email.trim(),
          password: formData.password,
          name: formData.name.trim() || formData.email.split('@')[0],
          accept_terms: acceptTerms || mode === 'login',
          mode,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Redemption failed');
        return;
      }
      if (data.token) localStorage.setItem('token', data.token);
      if (data.user && setUser) setUser(data.user);
      toast.success(data.message || `${planTitle} plan unlocked`);
      navigate('/dashboard');
    } catch (err) {
      toast.error('Network error — try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0a08] text-[#faf7f2] flex flex-col">
      <header className="px-5 py-4 flex items-center gap-3 border-b border-white/10">
        <button type="button" onClick={() => navigate('/')} className="text-stone-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Rocket className="w-5 h-5 text-[#fbbf0f] -rotate-45" strokeWidth={2.4} />
        <span className="font-semibold">Snagcart · AppSumo</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-6 shadow-xl">
          <h1 className="text-2xl font-bold tracking-tight">Activate your license</h1>
          <p className="mt-2 text-sm text-stone-400">
            Create an account (or log in) to unlock your AppSumo plan on Snagcart.
          </p>

          {sessionError ? (
            <div className="mt-6 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {sessionError}
              <p className="mt-2 text-stone-400">Need help? Email support@snagcart.io</p>
            </div>
          ) : !sessionInfo ? (
            <div className="mt-8 flex justify-center text-stone-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <>
              <div className="mt-4 rounded-lg border border-[#fbbf0f]/25 bg-[#fbbf0f]/10 px-3 py-2 text-sm">
                Plan unlocked: <span className="font-semibold text-[#fbbf0f]">{planTitle}</span>
                {sessionInfo.tier != null && (
                  <span className="text-stone-400"> · AppSumo tier {sessionInfo.tier}</span>
                )}
              </div>

              <div className="mt-5 flex gap-2 text-sm">
                <button
                  type="button"
                  className={`flex-1 rounded-md py-2 ${mode === 'register' ? 'bg-white text-black font-medium' : 'bg-white/5 text-stone-300'}`}
                  onClick={() => setMode('register')}
                >
                  Create account
                </button>
                <button
                  type="button"
                  className={`flex-1 rounded-md py-2 ${mode === 'login' ? 'bg-white text-black font-medium' : 'bg-white/5 text-stone-300'}`}
                  onClick={() => setMode('login')}
                >
                  Log in
                </button>
              </div>

              <form className="mt-5 space-y-4" onSubmit={onSubmit}>
                {mode === 'register' && (
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      className="mt-1 bg-black/40 border-white/10"
                      value={formData.name}
                      onChange={(e) => setFormData((s) => ({ ...s, name: e.target.value }))}
                      required
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    className="mt-1 bg-black/40 border-white/10"
                    value={formData.email}
                    onChange={(e) => setFormData((s) => ({ ...s, email: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    minLength={8}
                    className="mt-1 bg-black/40 border-white/10"
                    value={formData.password}
                    onChange={(e) => setFormData((s) => ({ ...s, password: e.target.value }))}
                    required
                  />
                </div>
                {mode === 'register' && (
                  <label className="flex items-start gap-2 text-xs text-stone-400">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <span>
                      I accept the{' '}
                      <a className="underline" href="/terms" target="_blank" rel="noreferrer">Terms</a>
                      {' '}and{' '}
                      <a className="underline" href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                    </span>
                  </label>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#fbbf0f] text-[#16130d] hover:bg-[#fbbf0f]/90 font-semibold"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'login' ? 'Log in & unlock' : 'Create account & unlock')}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
